/**
 * Careers CMS — list & manage job openings.
 * Design mirrors BlogListPage exactly (vscode theme variables, same table structure).
 * Navigation uses dashboard-internal paths (/dashboard/careers/*), never /admin/*.
 */

import { AnimatePresence } from 'framer-motion';
import {
  Briefcase,
  ExternalLink,
  Eye,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';

const PAGE_SIZE = 20;
type StatusFilter = 'all' | 'draft' | 'published' | 'closed' | 'archived';
const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'all',       label: 'All'       },
  { id: 'draft',     label: 'Draft'     },
  { id: 'published', label: 'Published' },
  { id: 'closed',    label: 'Closed'    },
  { id: 'archived',  label: 'Archived'  },
];

export interface CareerPost {
  id: string;
  slug: string;
  title: string;
  department: string | null;
  location: string | null;
  job_type: string | null;
  status: string;
  published_at: string | null;
  updated_at: string;
  created_at: string;
}

/** Same navigate-via-popstate pattern as BlogListPage */
const navigateTo = (path: string) => {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const CareersListInner: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const [searchInput, setSearchInput]     = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter]   = useState<StatusFilter>('all');
  const [page, setPage]                   = useState(1);
  const [rows, setRows]                   = useState<CareerPost[]>([]);
  const [total, setTotal]                 = useState(0);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [toast, setToast]                 = useState<string | null>(null);
  const [deletingId, setDeletingId]       = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string } | null>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to   = from + PAGE_SIZE - 1;

      let q = supabase
        .from('career_posts')
        .select('id,slug,title,department,location,job_type,status,published_at,updated_at,created_at', { count: 'exact' })
        .order('updated_at', { ascending: false });

      if (statusFilter !== 'all') q = q.eq('status', statusFilter);
      if (debouncedSearch.trim()) q = q.or(`title.ilike.%${debouncedSearch.trim()}%,slug.ilike.%${debouncedSearch.trim()}%`);

      const { data, error: err, count } = await q.range(from, to);
      if (err) throw err;
      setRows((data as CareerPost[]) ?? []);
      setTotal(count ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load openings');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    setDeletingId(id);
    try {
      const { error: delErr } = await supabase.from('career_posts').delete().eq('id', id);
      if (delErr) throw delErr;
      showToast('Opening deleted');
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const statusBadgeClass = (s: string) => {
    if (s === 'published') return 'ds-chip-ok';
    if (s === 'draft')     return '';
    if (s === 'closed')    return 'ds-chip-danger';
    return '';
  };

  return (
    <>
      <div className={embedded ? 'h-full ds-page' : 'min-h-screen ds-page'}>

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 right-6 z-50 ds-recessed-ok t-meta" role="status">
            {toast}
          </div>
        )}

        <div className={`${embedded ? '' : 'max-w-7xl mx-auto'} px-4 sm:px-6 py-6`}>

          {/* Header — same layout as BlogListPage embedded header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="t-h3 flex items-center gap-2">
                <Briefcase className="w-5 h-5" style={{ color: 'var(--ds-brand)' }} />
                {embedded ? 'Career' : 'Job Openings'}
              </h2>
              <p className="t-meta mt-0.5">
                Draft openings stay private. Published jobs appear on{' '}
                <a href="/careers" target="_blank" rel="noopener noreferrer" className="t-link inline-flex items-center gap-1">
                  gograbjob.com/careers <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <a
                href="/careers"
                target="_blank"
                rel="noopener noreferrer"
                className="ds-btn ds-btn-secondary w-full sm:w-auto"
              >
                <Eye className="w-4 h-4" />
                View careers page
              </a>
              <button
                type="button"
                onClick={() => void load()}
                title="Refresh"
                className="ds-btn ds-btn-secondary"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => navigateTo('/dashboard/careers/new')}
                className="ds-btn ds-btn-primary w-full sm:w-auto"
              >
                <Plus className="w-4 h-4" />
                New job
              </button>
            </div>
          </div>

          {/* Search + filter chips — identical to BlogListPage */}
          <div className="space-y-3 mb-5">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ds-text-muted)' }} />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title or slug…"
                className="ds-input pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setStatusFilter(opt.id)}
                  className="ds-chip ds-chip-button min-h-[44px] touch-manipulation"
                  aria-selected={statusFilter === opt.id ? 'true' : undefined}
                  data-inactive={statusFilter !== opt.id ? '' : undefined}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error banner */}
          {error && !loading && (
            <div className="mb-4 ds-recessed-warn t-meta">
              {error}
            </div>
          )}

          {/* Table — mirrors BlogListPage table exactly */}
          <div className="ds-card-flush">
            {loading && (
              <div className="py-20 flex justify-center">
                <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
                     style={{ borderColor: 'var(--ds-brand)', borderTopColor: 'transparent' }} />
              </div>
            )}
            {!loading && rows.length === 0 && !error && (
              <div className="py-16 text-center">
                <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="t-h3">No job openings found.</p>
                <button
                  type="button"
                  onClick={() => navigateTo('/dashboard/careers/new')}
                  className="mt-3 t-link"
                >
                  Create the first one →
                </button>
              </div>
            )}
            {!loading && rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="ds-table">
                  <thead>
                    <tr>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Slug</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Location</th>
                      <th className="px-4 py-3">Updated</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td className="font-medium max-w-[200px]">
                          <div className="truncate" title={r.title}>{r.title}</div>
                          {r.department && (
                            <div className="t-meta mt-0.5 truncate">{r.department}</div>
                          )}
                        </td>
                        <td className="t-mono t-tag max-w-[160px] truncate"
                            title={r.slug} style={{ color: 'var(--ds-brand)' }}>
                          {r.slug}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`ds-chip capitalize ${statusBadgeClass(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="t-meta max-w-[120px] truncate" title={r.location ?? ''}>
                          {r.location ?? '—'}
                        </td>
                        <td className="t-meta whitespace-nowrap">
                          {r.updated_at
                            ? new Date(r.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {r.status === 'published' && (
                            <a
                              href={`/careers/${r.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ds-btn ds-btn-ghost ds-btn-sm mr-2"
                              title="View on careers page"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => navigateTo(`/dashboard/careers/${r.id}/edit`)}
                            className="ds-btn ds-btn-ghost ds-btn-sm mr-3"
                            style={{ color: 'var(--ds-brand)' }}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            type="button"
                            disabled={deletingId === r.id}
                            onClick={() => setConfirmDelete({ id: r.id, title: r.title })}
                            className="ds-btn ds-btn-ghost ds-btn-sm hover:text-[var(--ds-danger)] disabled:opacity-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!loading && total > PAGE_SIZE && (
            <div className="mt-6 flex items-center justify-between t-meta">
              <span>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="ds-btn ds-btn-secondary ds-btn-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="ds-btn ds-btn-secondary ds-btn-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm modal */}
      <AnimatePresence>
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="ds-card-pop max-w-sm w-full">
              <h3 className="t-h3 mb-1">Delete opening?</h3>
              <p className="t-meta mb-5">
                "<span className="t-strong font-medium">{confirmDelete.title}</span>" will be permanently deleted.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="ds-btn ds-btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleDeleteConfirmed()}
                  disabled={!!deletingId}
                  className="ds-btn ds-btn-danger flex-1 disabled:opacity-50"
                >
                  {deletingId ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

const CareersListPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => (
  <CareersListInner embedded={embedded} />
);

export default CareersListPage;
